import React, { useEffect, useMemo, useState } from "react";

import { isAxiosError } from "axios";
import { Controller, FormProvider, useForm } from "react-hook-form";

import {
  useCreateCredential,
  useUpdateCredentialById,
} from "src/api/agents/credentials";
import { useAgentModels } from "src/api/agents/management";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import type { Option } from "src/base-components/Select";
import { Select } from "src/base-components/Select";
import type { CredentialItem } from "src/clients/agent-management";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { ModelIcon } from "src/genai/ModelSelector";
import { CredentialEnvironmentsConfig } from "src/settings/credentials/CredentialEnvironmentsConfig";
import { AnthropicFields } from "src/settings/credentials/credentialFields/AnthropicFields";
import { OpenAIFields } from "src/settings/credentials/credentialFields/OpenAIFields";
import { VertexFields } from "src/settings/credentials/credentialFields/VertexFields";
import {
  type AICredentialFormInputs,
  type CredentialEnvName,
  type CredentialProvider,
  convertBECredentialToFormInputs,
  convertFormInputsToBECredential,
  getDefaultEnvValues,
  getDefaultValues,
  omitUneditedSecrets,
} from "src/settings/credentials/model";
import { logger } from "src/utils/logger";

const CREDENTIAL_PROVIDERS: readonly CredentialProvider[] = [
  "openai",
  "google_vertex",
  "anthropic",
] as const;

const DEFAULT_SAVE_ERROR =
  "An error occurred while saving the credential. Please try again.";

// Pull a human-readable message out of an API error response, falling back to a
// generic message when the error isn't a structured Axios validation error.
export const extractErrorDescription = (error: unknown): string => {
  if (!isAxiosError(error) || !error.response?.data?.detail) {
    return DEFAULT_SAVE_ERROR;
  }
  const detail = error.response.data.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item: unknown) =>
        typeof item === "object" && item !== null && "msg" in item
          ? (item as { msg: string }).msg
          : String(item),
      )
      .join(". ");
  }
  return String(detail);
};

type Props = {
  open: boolean;
  credential?: CredentialItem;
  onClose: () => void;
};

const PROVIDER_LABELS: Partial<Record<CredentialProvider, string>> = {
  openai: "OpenAI",
  google_vertex: "Google Vertex AI",
  anthropic: "Anthropic",
};

const NAME_PLACEHOLDERS: Record<CredentialProvider, string> = {
  openai: "e.g. Production OpenAI",
  google_vertex: "e.g. Production Vertex AI",
  anthropic: "e.g. Production Anthropic",
};

const ProviderOptionLabel: React.FC<{ provider: CredentialProvider }> = ({
  provider,
}) => (
  <div className="flex items-center gap-2">
    <ModelIcon
      modelName={PROVIDER_LABELS[provider] ?? provider}
      provider={provider}
    />
    <span>{PROVIDER_LABELS[provider] ?? provider}</span>
  </div>
);

const PROVIDER_OPTIONS: Option[] = CREDENTIAL_PROVIDERS.map((provider) => ({
  key: provider,
  value: <ProviderOptionLabel provider={provider} />,
  searchText: PROVIDER_LABELS[provider],
}));

/** Form path of the secret input for a provider within an environment config. */
type SecretFieldPath =
  | `envConfigs.${CredentialEnvName}.apiKey`
  | `envConfigs.${CredentialEnvName}.serviceAccountJson`;

const secretFieldForProvider = (
  provider: CredentialProvider,
  env: CredentialEnvName,
): SecretFieldPath => {
  if (provider === "google_vertex") {
    return `envConfigs.${env}.serviceAccountJson`;
  }
  return `envConfigs.${env}.apiKey`;
};

export const UpsertCredentialModal: React.FC<Props> = ({
  open,
  credential,
  onClose,
}) => {
  const createCredential = useCreateCredential();
  const updateCredentialById = useUpdateCredentialById();
  const modelsQuery = useAgentModels();

  const isEditing = credential !== undefined;
  const [secretsEditable, setSecretsEditable] = useState<
    Record<string, boolean>
  >({});

  const methods = useForm<AICredentialFormInputs>({
    mode: "onChange",
    defaultValues: credential
      ? convertBECredentialToFormInputs(credential)
      : getDefaultValues("openai"),
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    unregister,
    clearErrors,
    formState: { errors, isSubmitting, isDirty, defaultValues },
  } = methods;

  const selectedProvider = watch("provider");
  const envConfigs = watch("envConfigs");
  const addedEnvironments = useMemo(
    () =>
      (Object.keys(envConfigs ?? {}) as CredentialEnvName[]).filter(
        (env) => envConfigs?.[env] !== undefined,
      ),
    [envConfigs],
  );
  const initialEnvKeys = useMemo(
    () => new Set(Object.keys(defaultValues?.envConfigs ?? {})),
    [defaultValues],
  );

  const providerModels = useMemo(() => {
    if (!modelsQuery.data) return [];
    return modelsQuery.data.filter(
      (m) => m.provider === selectedProvider && m.credential_id == null,
    );
  }, [modelsQuery.data, selectedProvider]);

  useEffect(() => {
    setSecretsEditable({});
    if (credential) {
      reset(convertBECredentialToFormInputs(credential));
    } else {
      reset(getDefaultValues("openai"));
    }
  }, [credential, reset]);

  const enableSecret = (fieldKey: SecretFieldPath) => {
    setSecretsEditable((prev) => ({ ...prev, [fieldKey]: true }));
    setValue(fieldKey, "", { shouldDirty: true });
  };

  const handleAddEnvironment = (env: CredentialEnvName) => {
    const nextEnvConfigs = {
      ...(getValues("envConfigs") ?? {}),
      [env]: getDefaultEnvValues(selectedProvider),
    };
    setValue("envConfigs", nextEnvConfigs, { shouldDirty: true });
    const secretField = secretFieldForProvider(selectedProvider, env);
    setSecretsEditable((prev) => ({ ...prev, [secretField]: true }));
  };

  const handleRemoveEnvironment = (env: CredentialEnvName) => {
    const nextEnvConfigs = { ...(getValues("envConfigs") ?? {}) };
    delete nextEnvConfigs[env];
    setValue("envConfigs", nextEnvConfigs, { shouldDirty: true });
    clearErrors(`envConfigs.${env}`);
    unregister(`envConfigs.${env}`);
    const secretField = secretFieldForProvider(selectedProvider, env);
    setSecretsEditable((prev) => {
      const next = { ...prev };
      delete next[secretField];
      return next;
    });
  };

  const isSecretEditable = (
    fieldKey: SecretFieldPath,
    env: CredentialEnvName,
  ) => {
    if (!isEditing || secretsEditable[fieldKey]) {
      return true;
    }
    // No stored secret yet for an env that wasn't present in the loaded
    // credential — show an empty editable field, not a mask.
    if (!initialEnvKeys.has(env)) {
      return true;
    }
    return false;
  };

  const requiredSecretFields = useMemo(
    () =>
      addedEnvironments.map((env) =>
        secretFieldForProvider(selectedProvider, env),
      ),
    [selectedProvider, addedEnvironments],
  );

  const editedSecretFields = requiredSecretFields.filter(
    (field) => secretsEditable[field],
  );

  const hasValidEditedSecrets =
    !isEditing ||
    editedSecretFields.every((field) => (watch(field) ?? "").trim() !== "");

  const secretsEditedByEnv = (): Record<CredentialEnvName, boolean> => ({
    live: Boolean(
      secretsEditable[secretFieldForProvider(selectedProvider, "live")],
    ),
    sandbox: Boolean(
      secretsEditable[secretFieldForProvider(selectedProvider, "sandbox")],
    ),
  });

  const modelOptions: Option[] = useMemo(
    () =>
      providerModels.map((model) => ({
        key: model.model_id,
        value: model.display_name,
      })),
    [providerModels],
  );

  const renderProviderFields = (
    provider: CredentialProvider,
    env: CredentialEnvName,
  ): React.ReactNode => {
    const secretField = secretFieldForProvider(provider, env);
    const secretEditable = isSecretEditable(secretField, env);

    switch (provider) {
      case "openai":
        return (
          <OpenAIFields
            env={env}
            isEditing={isEditing}
            secretEditable={secretEditable}
            onEnableSecret={() => enableSecret(secretField)}
            onRegionChange={() => {
              if (isEditing && !isSecretEditable(secretField, env)) {
                enableSecret(secretField);
              }
            }}
          />
        );
      case "google_vertex":
        return (
          <VertexFields
            env={env}
            isEditing={isEditing}
            secretEditable={secretEditable}
            onEnableSecret={() => enableSecret(secretField)}
          />
        );
      case "anthropic":
        return (
          <AnthropicFields
            env={env}
            isEditing={isEditing}
            secretEditable={secretEditable}
            onEnableSecret={() => enableSecret(secretField)}
          />
        );
    }
  };

  const hasLiveEnvironment = addedEnvironments.includes("live");

  const submitCredential = handleSubmit(
    async (form: AICredentialFormInputs) => {
      try {
        const upsert = convertFormInputsToBECredential(form);

        if (isEditing) {
          // Always send the credential payload so allow_test_invocations is
          // force-flipped to false (the field can only live inside `credential`
          // per the API contract). omitUneditedSecrets strips unedited secrets
          // so the backend backfills them from the stored values.
          await updateCredentialById.mutateAsync({
            credentialId: credential.id,
            data: {
              name: form.name,
              models: form.models,
              credential: omitUneditedSecrets(upsert, secretsEditedByEnv()),
            },
          });
        } else {
          await createCredential.mutateAsync({
            provider: form.provider,
            data: upsert,
          });
        }

        toastActions.success({
          title: isEditing ? "Credential updated" : "Credential created",
          description: `${PROVIDER_LABELS[form.provider]} credential saved successfully.`,
        });
        onClose();
      } catch (error) {
        logger.error(`Error when saving credential: ${error}`);

        toastActions.failure({
          title: "Failed to save credential",
          description: extractErrorDescription(error),
        });
      }
    },
  );

  return (
    <Modal
      afterLeave={() => {
        reset(getDefaultValues("openai"));
        setSecretsEditable({});
      }}
      open={open}
      size="md"
      onClose={onClose}
    >
      <Modal.Header>
        {isEditing ? "Update AI credential" : "Add AI credential"}
      </Modal.Header>
      <FormProvider {...methods}>
        <form onSubmit={submitCredential}>
          <Modal.Content>
            <FormItem gap="sm" label="Name" isRequired>
              <Input
                placeholder={NAME_PLACEHOLDERS[selectedProvider]}
                fullWidth
                {...register("name", {
                  required: "Name is required",
                  maxLength: {
                    value: 255,
                    message: "Name must be 255 characters or less",
                  },
                })}
              />
              {errors.name?.message && (
                <ErrorHint>{errors.name.message}</ErrorHint>
              )}
            </FormItem>

            <FormItem gap="sm" label="Provider">
              <Controller
                control={control}
                name="provider"
                render={({ field }) => (
                  <Select
                    disabled={isEditing}
                    options={PROVIDER_OPTIONS}
                    placeholder="Select a provider"
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value as CredentialProvider);
                      // Models are provider-scoped, so a fresh selection is
                      // required for the new provider. The name field is
                      // intentionally preserved across provider switches.
                      setValue("models", []);
                    }}
                  />
                )}
              />
            </FormItem>

            {modelOptions.length > 0 && (
              <FormItem gap="sm" label="Models" isRequired>
                {errors.models?.message && (
                  <ErrorHint>{errors.models.message}</ErrorHint>
                )}
                <Controller
                  control={control}
                  name="models"
                  shouldUnregister
                  rules={{
                    validate: (v) =>
                      (v && v.length > 0) ||
                      "At least one model must be selected",
                  }}
                  render={({ field }) => (
                    <Select
                      options={modelOptions}
                      placeholder="Select models this key can access"
                      value={field.value ?? []}
                      multiple
                      showSelectAllAsOption
                      onChange={(values) => field.onChange(values)}
                    />
                  )}
                />
              </FormItem>
            )}

            <CredentialEnvironmentsConfig
              addedEnvironments={addedEnvironments}
              hasEnvironmentError={(env) => Boolean(errors.envConfigs?.[env])}
              renderEnvironmentFields={(env) =>
                renderProviderFields(selectedProvider, env)
              }
              onAddEnvironment={handleAddEnvironment}
              onRemoveEnvironment={handleRemoveEnvironment}
            />
          </Modal.Content>
          <Modal.Footer
            primaryButton={
              <Button
                dataLoc="save-credential-button"
                isDisabled={
                  !isDirty ||
                  isSubmitting ||
                  !hasValidEditedSecrets ||
                  !hasLiveEnvironment
                }
                htmlType="submit"
                isLoading={isSubmitting}
                variant="primary"
              >
                {isEditing ? "Update" : "Create"}
              </Button>
            }
          />
        </form>
      </FormProvider>
    </Modal>
  );
};
